import type { SearchParameters } from '../types';
import type { SearchIntent } from '../types';

/**
 * Prompt variants for the LLM path. remove_filter and execute are handled
 * deterministically in nlp-processor and do not need dedicated prompts.
 */
export type PromptIntentVariant = Exclude<SearchIntent, 'remove_filter' | 'execute'> | 'general';

export interface PromptIntentHintContext {
  query: string;
  currentParams: SearchParameters;
  recentMessages?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

const CONFIRM_QUERY_PATTERN =
  /^(yes|yeah|yep|sure|ok|okay|correct|that's right|that sounds good|do it|go ahead|please do|sounds good)\.?$/i;

const MODIFY_QUERY_PATTERN =
  /\b(instead|actually|change|make it|switch to|rather than|update to|what about)\b/i;

const ADD_QUERY_PATTERN =
  /\b(add|include|also|plus|as well|with a|with an|and also|as well as)\b/i;

const NEW_SEARCH_QUERY_PATTERN =
  /\b(new search|start over|start fresh|reset all|from scratch)\b/i;

const CLARIFY_QUERY_PATTERN =
  /^(help|\?|huh|what\??|idk|i don't know)$/i;

function lastAssistantMessage(
  recentMessages?: Array<{ role: 'user' | 'assistant'; content: string }>
): string | undefined {
  if (!recentMessages?.length) {
    return undefined;
  }
  for (let i = recentMessages.length - 1; i >= 0; i -= 1) {
    if (recentMessages[i].role === 'assistant') {
      return recentMessages[i].content;
    }
  }
  return undefined;
}

function assistantAskedYesNoQuestion(assistantContent: string | undefined): boolean {
  if (!assistantContent) {
    return false;
  }
  return /\?\s*$/.test(assistantContent.trim()) ||
    /\b(would you like|do you mean|should i|want me to|add a|add an)\b/i.test(assistantContent);
}

function hasActiveSearchParams(currentParams: SearchParameters): boolean {
  return Object.keys(currentParams).length > 0;
}

/**
 * Heuristic intent hint for which focused system prompt to load before the LLM call.
 * The model still chooses the final intent in its JSON response.
 */
export function inferPromptIntentHint({
  query,
  currentParams,
  recentMessages,
}: PromptIntentHintContext): PromptIntentVariant {
  const trimmed = query.trim();
  if (!trimmed) {
    return 'general';
  }

  if (NEW_SEARCH_QUERY_PATTERN.test(trimmed)) {
    return 'new_search';
  }

  if (CLARIFY_QUERY_PATTERN.test(trimmed)) {
    return 'clarify';
  }

  const lastAssistant = lastAssistantMessage(recentMessages);
  if (CONFIRM_QUERY_PATTERN.test(trimmed) && assistantAskedYesNoQuestion(lastAssistant)) {
    return 'confirm';
  }

  const hasParams = hasActiveSearchParams(currentParams);

  if (hasParams && MODIFY_QUERY_PATTERN.test(trimmed)) {
    return 'modify_filter';
  }

  if (hasParams && ADD_QUERY_PATTERN.test(trimmed)) {
    return 'add_filter';
  }

  if (!hasParams) {
    return 'new_search';
  }

  if (hasParams) {
    return 'add_filter';
  }

  return 'general';
}
